// src/components/common/PageHelp.tsx
import React, { useState } from 'react';
import {
  IconButton,
  Dialog,
  DialogContent,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton as CloseButton,
} from '@mui/material';
import {
  HelpOutline,
  Close,
  CheckCircle,
  TipsAndUpdates,
  Lightbulb,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';

export interface PageHelpConfig {
  /** Translation key for the page title */
  titleKey: string;
  /** Translation key for description */
  descriptionKey: string;
  /** Translation keys for features (max 4 recommended) */
  featureKeys?: string[];
  /** Translation keys for quick tips (max 3 recommended) */
  tipKeys?: string[];
}

interface PageHelpProps {
  /** Help configuration with translation keys */
  config: PageHelpConfig;
  /** Icon size */
  size?: 'small' | 'medium' | 'large';
  /** Icon color */
  color?: 'primary' | 'secondary' | 'default';
}

/**
 * PageHelp Component - Redesigned
 * 
 * Modern, concise contextual help with full translation support.
 * Shows an info icon that opens a visual modal with key information.
 * 
 * Usage:
 * <PageHelp config={dashboardHelpConfig} />
 */
export const PageHelp: React.FC<PageHelpProps> = ({ 
  config, 
  size = 'small',
  color = 'primary' 
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      {/* Help Icon Button */}
      <Tooltip title={translate('common.help.tooltip')} arrow>
        <IconButton
          onClick={handleOpen}
          size={size}
          sx={{
            border: 'none',
            color: 'text.disabled',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            },
          }}
        >
          <HelpOutline fontSize={size} />
        </IconButton>
      </Tooltip>

      {/* Help Dialog - Redesigned */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={fullScreen}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
          
          }
        }}
      >
        {/* Close button */}
        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
          <CloseButton onClick={handleClose} size="small">
            <Close />
          </CloseButton>
        </Box>

        <DialogContent sx={{ pt: 4, pb: 3, px: 3 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                color: 'warning.main',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <HelpOutline sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {translate(config.titleKey)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
              {translate(config.descriptionKey)}
            </Typography>
          </Box>

          {/* Features Section */}
          {config.featureKeys && config.featureKeys.length > 0 && (
            <Card
              sx={{
                mb: 2,
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                background: alpha(theme.palette.success.main, 0.02),
              }}
            >
              <CardContent sx={{ py: 2, px: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {translate('common.help.keyFeatures')}
                  </Typography>
                </Box>
                <Grid container spacing={1.5}>
                  {config.featureKeys.map((key, index) => (
                    <Grid item xs={12} key={index}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <CheckCircle sx={{ fontSize: 16, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {translate(key)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Tips Section */}
          {config.tipKeys && config.tipKeys.length > 0 && (
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                background: alpha(theme.palette.warning.main, 0.02),
              }}
            >
              <CardContent sx={{ py: 2, px: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Lightbulb sx={{ fontSize: 20, color: 'warning.main' }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {translate('common.help.quickTips')}
                  </Typography>
                </Box>
                <Grid container spacing={1.5}>
                  {config.tipKeys.map((key, index) => (
                    <Grid item xs={12} key={index}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Lightbulb sx={{ fontSize: 16, color: 'warning.main', mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {translate(key)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};